import type { ChatHistoryItem } from '~/lib/persistence';
import type { StoreFileAction } from '~/types/actions';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

export function extractFrameworks(messageData: ChatHistoryItem): {
  framework: string;
  language: string;
} {
  // console.log('Starting extractFrameworks with messageData:',   messageData);

  const result = {
    framework: 'unknown',
    language: 'unknown',
  };

  // console.log('Initialized result:', result);

  const packageJson = messageData.fileActions.find(
    (action: StoreFileAction) => action.type === 'file' && action.filePath === 'package.json',
  );

  // console.log('Found packageJson:', packageJson);

  const hasMainDart = messageData.fileActions.find(
    (action: StoreFileAction) => action.type === 'file' && action.filePath === 'main.dart',
  );

  // console.log('hasMainDart:', hasMainDart);

  const hasHtmlFile = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.endsWith('.html'),
  );

  // console.log('hasHtmlFile:', hasHtmlFile);

  const hasTailwindConfig = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('tailwind.config'),
  );

  // console.log('hasTailwindConfig:', hasTailwindConfig);

  const hasCssFile = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('style.css'),
  );

  // console.log('hasCssFile:', hasCssFile);

  const hasMainJs = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('main.js'),
  );

  // console.log('hasMainJs:', hasMainJs);

  const hasViteConfig = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('vite.config.ts'),
  );

  // console.log('hasViteConfig:', hasViteConfig);

  const hasAngularJson = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('angular.json'),
  );

  // console.log('hasAngularJson:', hasAngularJson);

  const hasTsConfig = messageData.fileActions.some(
    (action: StoreFileAction) => action.type === 'file' && action.filePath.includes('tsconfig.json'),
  );

  // console.log('hasTsConfig:', hasTsConfig);

  // Check for Flutter first since it won't have package.json
  if (hasMainDart) {
    // console.log('Flutter detected');
    result.framework = 'flutter';
    result.language = 'dart';
  } else if (hasTsConfig) {
    // console.log('TypeScript detected');
    result.language = 'ts';
  } else if (!hasTsConfig) {
    // console.log('JavaScript detected');
    result.language = 'js';
  }

  try {
    // console.log('Entering try block for framework detection');

    if (hasAngularJson) {
      // console.log('Angular detected');
      result.framework = 'angular_css';
    } else if (!hasViteConfig && hasMainJs && hasHtmlFile) {
      // console.log('HTML project detected');
      result.framework = hasTailwindConfig ? 'html_tailwind' : 'html_css';
    }

    if (!packageJson) {
      // console.log('No package.json found, returning result:', result);
      return result;
    }

    const packageData = JSON.parse(packageJson.content);

    // console.log('Parsed package.json data:', packageData);

    // Check for React Native
    if (packageData.dependencies?.react && packageData.dependencies?.['react-native']) {
      // console.log('React Native detected');
      result.framework = 'reactnative';
    }
    // Check for React and its UI libraries
    else if (packageData.dependencies?.react) {
      // console.log('React detected, checking UI libraries');

      if (packageData.dependencies?.['@chakra-ui/react']) {
        // console.log('Chakra UI detected');
        result.framework = 'react_chakra';
      } else if (packageData.devDependencies?.tailwindcss) {
        // console.log('Checking for Shadcn configuration');

        const hasShadcnConfig = messageData.fileActions.some(
          (action: StoreFileAction) =>
            (action.type === 'file' && action.filePath === 'components.json') ||
            (action.content?.includes('@radix-ui') && action.content?.includes('clsx')),
        );

        // console.log('hasShadcnConfig:', hasShadcnConfig);
        result.framework = hasShadcnConfig ? 'react_shadcn' : 'react_tailwind';
      }

      // console.log('Returning React result:', result);
      return result;
    }
    // Check for Vue
    else if (packageData.dependencies?.vue) {
      // console.log('Vue detected');
      result.framework = packageData.devDependencies?.tailwindcss ? 'vue_tailwind' : 'vue_css';

      // console.log('Returning Vue result:', result);
      return result;
    }

    // console.log('No specific framework detected, returning result:', result);
    return result;
  } catch (error) {
    // console.error('Error parsing package.json:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error from extractFrameworks',
        packageJsonContent: packageJson?.content,
      },
    });
    return result;
  }
}
